import { useEffect, useRef, useState } from 'react';
import { ClipboardDocumentIcon, CheckIcon } from '@heroicons/react/24/outline';
import { Button } from './Button';

export interface CopyFieldProps {
  /** Accessible label for the readonly text box (visually hidden if `srLabel`). */
  label: string;
  /** Renders `label` visually (default). Set `false` for a caller that already
   * shows its own adjacent heading and only needs the control's a11y name. */
  showLabel?: boolean;
  value: string;
  /** How long the "Copied" confirmation stays up before reverting, in ms. */
  confirmMs?: number;
}

// CopyField (story 6-1a) — a readonly value with one-tap copy-to-clipboard,
// first needed for the share-link creation dialog's one-time URL display (P06
// §6.1: "primary 'Create link' → copy field with one-tap copy"). Generic over
// any secret/URL a caller wants to hand over without requiring a manual
// select-all — the value itself is never re-derived or reformatted here, so a
// copy is guaranteed byte-identical to what is shown.
//
// a11y: the text box is `readOnly`, not `disabled` — a disabled input is
// unfocusable and unreadable by assistive tech that skips disabled controls,
// exactly wrong for a value the whole point is to expose. The copy button's
// label swaps to "Copied" and the confirmation is also announced via a
// `role="status"` live region (polite), so a screen-reader user gets the same
// feedback a sighted user reads visually. Falls back to `document.execCommand`
// only if the async Clipboard API is unavailable (non-HTTPS/older embeds) —
// never silently failing.
export function CopyField({ label, showLabel = true, value, confirmMs = 2000 }: CopyFieldProps): JSX.Element {
  const [copied, setCopied] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  const copy = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(value);
      } else if (inputRef.current) {
        inputRef.current.select();
        // Documented fallback only (non-HTTPS/older embeds where the async
        // Clipboard API is unavailable) — `execCommand` is deprecated but
        // still the only synchronous copy path in that case.
        document.execCommand('copy');
      }
      setCopied(true);
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setCopied(false), confirmMs);
    } catch {
      // Clipboard write can be denied by the browser (permissions, focus
      // loss) — leave `copied` false rather than claim success that did not
      // happen (G-27's "never claim a false Saved" rule applies here too).
    }
  };

  return (
    <div className="space-y-1.5">
      <label htmlFor={label} className={showLabel ? 'text-sm font-medium text-foreground' : 'sr-only'}>
        {label}
      </label>
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          id={label}
          type="text"
          readOnly
          value={value}
          onFocus={(e) => e.currentTarget.select()}
          className="focus-ring w-full min-w-0 truncate rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground"
        />
        <Button
          type="button"
          variant="secondary"
          icon={copied ? <CheckIcon className="h-4 w-4" /> : <ClipboardDocumentIcon className="h-4 w-4" />}
          onClick={copy}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      <p role="status" className="sr-only">
        {copied ? 'Copied to clipboard' : ''}
      </p>
    </div>
  );
}
